import { useResource } from '@gc/ui-state';
import type { AuditEntry } from '@gc/api-client';

/**
 * A client reading their own activity history. Row-level security confines this to their
 * organisation, so there is no filter to get wrong here — the database will not return
 * another tenant's rows whatever this component asks for.
 */

export interface AuditApi {
  listAudit: () => Promise<{ items: AuditEntry[] }>;
}

export function Audit({ api }: { api: AuditApi }) {
  const { load } = useResource(() => api.listAudit(), [api]);

  if (load.kind === 'loading') return <p>Loading history…</p>;
  if (load.kind === 'error') return <p data-testid="audit-error">Could not load history: {load.message}</p>;
  const items = load.data.items;

  return (
    <section>
      <h2>Activity history</h2>
      <p>Every change to your organisation's records. This history cannot be edited or deleted by anyone, including us.</p>

      {items.length === 0
        ? <p data-testid="audit-empty">Nothing has been recorded yet.</p>
        : (
          <div className="scroll-x"><table>
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">What</th>
                <th scope="col">Record</th>
                <th scope="col">Source</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} data-testid={`audit-${e.id}`}>
                  <td><time dateTime={e.occurredAt}>{formatWhen(e.occurredAt)}</time></td>
                  <td>{e.actorSub}</td>
                  <td>{e.action}</td>
                  <td>{e.recordType}{e.recordId ? ` ${e.recordId.slice(0, 8)}` : ''}</td>
                  <td>{e.source}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
    </section>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
